import { Link } from "@remix-run/react";
import { useTranslation } from "react-i18next";
import { type DocIndexItem } from "#app/lib/docs/loader";
import { cn } from "#app/utils/misc";

const categoryIcons: Record<string, string> = {
  "Start Here": "🚀",
  "Product Guide": "📖",
  "Integration Guides": "🔌",
  Guides: "🧭",
  "How-to": "🛠️",
  Reference: "📚",
  About: "ℹ️",
};

export function DocSidebarNav({
  docs,
  currentSlug,
}: {
  docs: DocIndexItem[];
  currentSlug?: string;
}) {
  const { t } = useTranslation("docs");
  const grouped = docs.reduce<Record<string, DocIndexItem[]>>((acc, d) => {
    const key = (d.metadata.category || "Other").trim() || "Other";
    acc[key] ||= [];
    acc[key]!.push(d);
    return acc;
  }, {});

  // Define category order matching the mockup
  const categoryOrder = [
    "Start Here",
    "Product Guide",
    "Integration Guides",
    "Guides",
    "How-to",
    "Reference",
    "About",
  ];
  const categories = categoryOrder.filter((cat) => grouped[cat]);
  const labels: Record<string, string> = {
    "Start Here": t("categories.startHere"),
    "Product Guide": t("categories.productGuide"),
    "Integration Guides": t("categories.integrationGuides"),
    Guides: t("categories.guides"),
    "How-to": t("categories.howTos"),
    Reference: t("categories.reference"),
    About: t("categories.about"),
  };

  return (
    <nav className="space-y-6">
      {categories.map((cat) => (
        <div key={cat} className="space-y-2">
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-zinc-400 uppercase tracking-wider">
            <span className="text-sm">{categoryIcons[cat] || "📄"}</span>
            {labels[cat] ?? cat}
          </div>
          <div className="space-y-0.5">
            {grouped[cat]!.map((d) => {
              const isActive = currentSlug === d.slug;
              return (
                <Link
                  key={d.slug}
                  to={`/docs/${d.slug}`}
                  className={cn(
                    "block px-3 py-2 rounded-md text-sm transition-colors",
                    isActive
                      ? "relative text-[#535EA4] font-medium bg-[linear-gradient(90deg,#F0F4FF_0%,#FFFFFF_100%)] before:absolute before:left-0 before:top-1 before:bottom-1 before:w-1 before:rounded-full before:bg-[linear-gradient(180deg,#535EA4_0%,#909CCA_100%)] dark:bg-none dark:bg-zinc-800 dark:text-indigo-300"
                      : "text-gray-700 hover:bg-gray-50 hover:text-[#141A42] dark:text-zinc-300 dark:hover:bg-zinc-800",
                  )}
                >
                  {d.metadata.title}
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}
